import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlug, faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { usePrinter } from '../../contexts/PrinterContext';
import { loadPrinterSettings, savePrinterSettings } from '../../utils/storage';
import './PrinterSettings.css';

interface PrinterSettingsProps {
  isModal?: boolean;
}

interface PrinterSettingsData {
  defaultPrintMethod: 'thermal' | 'browser';
  autoConnect: boolean;
  debugMode: boolean;
}

const defaultSettings: PrinterSettingsData = {
  defaultPrintMethod: 'thermal',
  autoConnect: false,
  debugMode: false
};

const PrinterSettings: React.FC<PrinterSettingsProps> = ({ isModal = false }) => {
  const { 
    printer, 
    status, 
    isConnecting, 
    connectPrinter, 
    disconnectPrinter,
    debugMode,
    setDebugMode,
    debugInfo,
    setDebugInfo
  } = usePrinter();

  const [settings, setSettings] = useState<PrinterSettingsData>(defaultSettings);
  const [errorDetails, setErrorDetails] = useState<string>('');
  const [saveMessage, setSaveMessage] = useState<string>('');

  // Define connect function with useCallback to avoid dependency issues
  const handleConnectPrinter = React.useCallback(async () => {
    setErrorDetails('');
    await connectPrinter();
  }, [connectPrinter, setErrorDetails]);

  const handleDisconnectPrinter = React.useCallback(async () => {
    if (!printer) return;
    await disconnectPrinter();
  }, [printer, disconnectPrinter]);

  // Load settings on component mount
  useEffect(() => {
    const savedSettings = loadPrinterSettings();
    if (savedSettings) {
      setSettings(savedSettings);
      
      // Apply debug mode setting from saved settings
      if (savedSettings.debugMode !== debugMode) {
        setDebugMode(savedSettings.debugMode);
      }
    }
  }, [debugMode, setDebugMode]);
  
  // Handle auto-connect separately to avoid dependency cycle
  useEffect(() => {
    const savedSettings = loadPrinterSettings();
    if (savedSettings?.autoConnect && !printer) {
      handleConnectPrinter();
    }
  }, [printer, handleConnectPrinter]);

  // Functions already defined above

  const handleSettingChange = (key: keyof PrinterSettingsData, value: any) => {
    setSettings(prev => {
      const newSettings = { ...prev, [key]: value };
      savePrinterSettings(newSettings);
      
      // Apply debug mode setting immediately
      if (key === 'debugMode') {
        setDebugMode(value);
        if (value) {
          setDebugInfo('Debug mode enabled');
        } else {
          setDebugInfo('');
        }
      }
      
      // Show save confirmation
      setSaveMessage('Settings saved!');
      setTimeout(() => setSaveMessage(''), 3000);
      
      return newSettings;
    });
  };

  const containerClass = isModal ? 'printer-settings-modal' : 'printer-settings-section';

  return (
    <div className={containerClass}>
      {!isModal && <h3>Printer Settings</h3>}
      
      <div className="printer-settings-content">
        <div className="printer-connection-status">
          <h4>Printer Connection</h4>
          <div className={`status-indicator ${printer ? 'connected' : 'disconnected'}`}>
            <FontAwesomeIcon icon={faPlug} /> {printer ? 'Connected' : 'Disconnected'}
          </div>
          
          {status && (
            <div className="status-message">
              <FontAwesomeIcon 
                icon={status.includes('Error') ? faExclamationCircle : 
                      status.includes('connected') ? faCheckCircle : 
                      faPlug} 
                className={status.includes('Error') ? 'error' : 
                          status.includes('connected') ? 'success' : ''}
              />
              <span>{status}</span>
            </div>
          )}
          
          {errorDetails && (
            <div className="error-details">
              <pre>{errorDetails}</pre>
            </div>
          )}
          
          <div className="printer-buttons">
            {!printer ? (
              <button
                className="connect-button"
                onClick={handleConnectPrinter}
                disabled={isConnecting}
              >
                {isConnecting ? 'Connecting...' : 'Connect to Printer'}
              </button>
            ) : (
              <button
                className="disconnect-button"
                onClick={handleDisconnectPrinter}
              >
                Disconnect Printer
              </button>
            )}
          </div>
        </div>
        
        <div className="printer-options">
          <h4>Print Options</h4>
          
          <div className="setting-option">
            <label>Default Print Method:</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="defaultPrintMethod"
                  value="thermal"
                  checked={settings.defaultPrintMethod === 'thermal'}
                  onChange={() => handleSettingChange('defaultPrintMethod', 'thermal')}
                />
                Thermal Printer
              </label>
              <label>
                <input
                  type="radio"
                  name="defaultPrintMethod"
                  value="browser"
                  checked={settings.defaultPrintMethod === 'browser'}
                  onChange={() => handleSettingChange('defaultPrintMethod', 'browser')}
                />
                Browser Print
              </label>
            </div>
          </div>
          
          <div className="setting-option">
            <label>
              <input
                type="checkbox"
                checked={settings.autoConnect}
                onChange={(e) => handleSettingChange('autoConnect', e.target.checked)}
              />
              Auto-connect to printer when app starts
            </label>
          </div>
          
          <div className="setting-option">
            <label>
              <input
                type="checkbox"
                checked={settings.debugMode}
                onChange={(e) => handleSettingChange('debugMode', e.target.checked)}
              />
              Enable Debug Mode
            </label>
          </div>
        </div>
        
        {debugMode && debugInfo && (
          <div className="debug-info">
            <h4>Debug Information</h4>
            <pre>{debugInfo}</pre>
          </div>
        )}
        
        <div className="printer-info">
          <h4>Printer Information</h4>
          <p>
            <strong>Note:</strong> This application requires a compatible ESC/POS thermal printer
            connected via USB. Make sure your printer is connected and powered on before clicking
            "Connect to Printer".
          </p>
          {!printer && (
            <p className="browser-support-warning">
              <strong>Browser Support:</strong> WebUSB is required for printer connectivity.
              Chrome, Edge, and Opera are fully supported. Firefox and Safari do not currently
              support WebUSB.
            </p>
          )}
        </div>
        
        {saveMessage && (
          <div className="save-message success">
            <FontAwesomeIcon icon={faCheckCircle} /> {saveMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default PrinterSettings;
